/**
 * API Service for Auxilio Travel
 * Handles all HTTP requests to the backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

// Determine API base URL
const getBaseURL = (): string => {
  // In production (served via nginx), use /api prefix
  if (window.location.port === '80' || window.location.port === '3005' || window.location.port === '') {
    return '/api';
  }
  // In development, connect directly to backend
  return 'http://localhost:8005';
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and language preference
apiClient.interceptors.request.use(
  (config) => {
    const authData = localStorage.getItem('auxilio-travel-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }

    // Add language preference from i18next
    const language = localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem('auxilio-travel-auth');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API functions
export const authAPI = {
  login: async (credentials: { username: string; email: string; password: string }) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  register: async (data: { username: string; email: string; password: string }) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  verifyEmail: async (email: string, code: string) => {
    const response = await apiClient.post('/auth/verify-email', { email, code });
    return response.data;
  },

  resendVerificationEmail: async (email: string) => {
    const response = await apiClient.post('/auth/resend-verification-email', { email });
    return response.data;
  },

  resetPassword: async (email: string, newPassword: string) => {
    const response = await apiClient.post('/auth/reset-password', { email, new_password: newPassword });
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
};

// Travel Glossary API functions
export const glossaryAPI = {
  getTerms: async () => {
    const response = await apiClient.get('/travel-glossary/terms');
    return response.data;
  },

  getCategories: async () => {
    const response = await apiClient.get('/travel-glossary/categories');
    return response.data;
  },

  searchTerms: async (query: string) => {
    const response = await apiClient.get(`/travel-glossary/search?query=${encodeURIComponent(query)}`);
    return response.data;
  },

  explainTerm: async (term: string, simpleMode: boolean = false) => {
    const response = await apiClient.post('/travel-glossary/explain', { term, simple_mode: simpleMode });
    return response.data;
  },

  addUserTerm: async (term: string, definition: string, category?: string) => {
    const response = await apiClient.post('/travel-glossary/user-terms', { term, definition, category });
    return response.data;
  },

  getUserTerms: async () => {
    const response = await apiClient.get('/travel-glossary/user-terms');
    return response.data;
  },

  deleteUserTerm: async (termId: string) => {
    const response = await apiClient.delete(`/travel-glossary/user-terms/${termId}`);
    return response.data;
  },

  suggestCategory: async (term: string, definition: string) => {
    const response = await apiClient.post('/travel-glossary/suggest-category', { term, definition });
    return response.data;
  },
};

// Travel Questions API functions
export const questionsAPI = {
  askQuestion: async (data: {
    category: string;
    question: string;
    travel_style?: string;
    trip_type?: string;
    simpleMode?: boolean;
  }) => {
    const response = await apiClient.post('/travel-questions/ask', data);
    return response.data;
  },

  followUp: async (data: {
    session_id: string;
    question: string;
    context?: string;
  }) => {
    const response = await apiClient.post('/travel-questions/follow-up', data);
    return response.data;
  },

  getCategories: async () => {
    const response = await apiClient.get('/travel-questions/categories');
    return response.data;
  },

  getSessions: async () => {
    const response = await apiClient.get('/travel-questions/sessions');
    return response.data;
  },
};

// Calculator API functions
export const calculatorAPI = {
  convertCurrency: async (data: {
    from_currency: string;
    to_currency: string;
    amount: number;
  }) => {
    const response = await apiClient.post('/calculators/currency', data);
    return response.data;
  },

  estimateBudget: async (data: {
    destination: string;
    days: number;
    budget_style: string;
    travelers: number;
  }) => {
    const response = await apiClient.post('/calculators/budget', data);
    return response.data;
  },

  calculateTip: async (data: {
    country: string;
    bill_amount: number;
    service_type?: string;
    currency?: string;
    split_ways?: number;
  }) => {
    const response = await apiClient.post('/calculators/tip', data);
    return response.data;
  },

  convertTimezone: async (data: {
    from_timezone: string;
    to_timezone: string;
    datetime: string;
  }) => {
    const response = await apiClient.post('/calculators/timezone', data);
    return response.data;
  },

  calculateDistance: async (data: {
    from_city: string;
    to_city: string;
  }) => {
    const response = await apiClient.post('/calculators/distance', data);
    return response.data;
  },
};

// Destinations API functions
export const destinationsAPI = {
  search: async (params: {
    q: string;
    region?: string;
    budget?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.get('/destinations/search', { params });
    return response.data;
  },

  getProfile: async (destinationId: string) => {
    const response = await apiClient.get(`/destinations/${destinationId}`);
    return response.data;
  },

  getPOIs: async (destinationId: string, data: {
    category?: string;
    include_hidden_gems?: boolean;
  } = {}) => {
    const response = await apiClient.post(`/destinations/${destinationId}/pois`, data);
    return response.data;
  },

  getPhotos: async (destinationId: string, page: number = 1, perPage: number = 9) => {
    const response = await apiClient.get(`/destinations/${destinationId}/photos`, {
      params: { page, per_page: perPage }
    });
    return response.data;
  },

  getWeather: async (destinationId: string) => {
    const response = await apiClient.get(`/destinations/${destinationId}/weather`);
    return response.data;
  },

  compare: async (data: {
    destination_ids: string[];
    include_ai_recommendation?: boolean;
    preferences?: Record<string, string | string[]>;
    custom_destinations?: Array<{
      id: string;
      city: string;
      country: string;
      region?: string;
      tagline?: string;
      budget_level?: string;
    }>;
  }) => {
    const response = await apiClient.post('/destinations/compare', data);
    return response.data;
  },

  getAISummary: async (data: {
    destination_id: string;
    focus?: string;
    city?: string;
    country?: string;
  }) => {
    const response = await apiClient.post('/destinations/ai-summary', data);
    return response.data;
  },

  enrichCustomDestination: async (data: {
    city: string;
    country: string;
    country_code?: string;
    region?: string;
    lat: number;
    lon: number;
  }) => {
    const response = await apiClient.post('/destinations/enrich', data);
    return response.data;
  },

  getPhotosByQuery: async (city: string, country: string, page: number = 1, perPage: number = 9) => {
    const response = await apiClient.get('/destinations/photos-by-query', {
      params: { city, country, page, per_page: perPage }
    });
    return response.data;
  },
};

// Itinerary API functions
export const itineraryAPI = {
  generate: async (data: {
    destinations?: string[];
    destination_entries?: Array<{ name: string; days: number }>;
    duration_days: number;
    travel_style: string;
    budget_level: string;
    interests: string[];
    group_type: string;
    mobility_notes?: string;
    start_date?: string;
  }) => {
    const response = await apiClient.post('/itineraries/generate', data);
    return response.data;
  },

  suggest: async (data: {
    itinerary: Record<string, unknown>;
    focus_area?: string;
  }) => {
    const response = await apiClient.post('/itineraries/suggest', data);
    return response.data;
  },

  optimize: async (data: {
    itinerary: Record<string, unknown>;
  }) => {
    const response = await apiClient.post('/itineraries/optimize', data);
    return response.data;
  },

  getTemplates: async (params?: {
    region?: string;
    duration_min?: number;
    duration_max?: number;
    style?: string;
  }) => {
    const response = await apiClient.get('/itineraries/templates', { params });
    return response.data;
  },

  calculateRoutes: async (data: { itinerary: Record<string, unknown> }) => {
    const response = await apiClient.post('/itineraries/routes', data);
    return response.data;
  },

  getTransportTips: async (data: { segments: Array<{ from_city: string; to_city: string; distance_km?: number }> }) => {
    const response = await apiClient.post('/itineraries/transport-tips', data);
    return response.data;
  },
};

// Weather Intelligence API functions
export const weatherAPI = {
  getCurrentWeather: async (location: string) => {
    const response = await apiClient.get(`/weather/current/${encodeURIComponent(location)}`);
    return response.data;
  },

  getForecast: async (location: string, params: {
    days?: number;
  } = {}) => {
    const response = await apiClient.get(`/weather/forecast/${encodeURIComponent(location)}`, { params });
    return response.data;
  },

  getHistorical: async (location: string) => {
    const response = await apiClient.get(`/weather/historical/${encodeURIComponent(location)}`);
    return response.data;
  },

  generatePackingList: async (data: {
    destination: string;
    start_date?: string;
    end_date?: string;
    duration_days?: number;
    activities?: string[];
    travelers?: number;
  }) => {
    const response = await apiClient.post('/weather/packing-list', data);
    return response.data;
  },

  compareWeather: async (data: {
    locations: string[];
  }) => {
    const response = await apiClient.post('/weather/compare', data);
    return response.data;
  },
};

// Flight & Route Explorer API functions
export const flightsAPI = {
  routeResearch: async (data: { origin: string; destination: string }) => {
    const response = await apiClient.post('/flights/route-research', data);
    return response.data;
  },

  tips: async (data: {
    origin: string;
    destination: string;
    travel_month?: string;
    cabin_class?: string;
  }) => {
    const response = await apiClient.post('/flights/tips', data);
    return response.data;
  },

  airportGuide: async (data: { query: string }) => {
    const response = await apiClient.post('/flights/airport-guide', data);
    return response.data;
  },

  bookingLinks: async (data: { origin: string; destination: string }) => {
    const response = await apiClient.post('/flights/booking-links', data);
    return response.data;
  },
};

// Accommodation Guide API functions
export const accommodationsAPI = {
  types: async (data: { destination: string; budget_level?: string }) => {
    const response = await apiClient.post('/accommodations/types', data);
    return response.data;
  },

  neighborhoods: async (data: {
    destination: string;
    interests?: string[];
    budget_level?: string;
  }) => {
    const response = await apiClient.post('/accommodations/neighborhoods', data);
    return response.data;
  },

  recommend: async (data: {
    destination: string;
    budget_level: string;
    travel_style?: string;
    interests?: string[];
    group_type?: string;
    duration_days?: number;
  }) => {
    const response = await apiClient.post('/accommodations/recommend', data);
    return response.data;
  },

  bookingLinks: async (data: { destination: string }) => {
    const response = await apiClient.post('/accommodations/booking-links', data);
    return response.data;
  },

  find: async (data: AccommodationFinderRequest): Promise<AccommodationFinderResponse> => {
    const response = await apiClient.post('/accommodations/find', data);
    return response.data;
  },
};

// Accommodation Finder types
export interface AccommodationFinderRequest {
  destination: string;
  neighborhood?: string;
  check_in?: string;
  check_out?: string;
  nights?: number;
  accommodation_types?: string[];
  budget_level: string;
  room_preferences?: string[];
  arrival_mode?: string;
  group_type?: string;
  other_preferences?: string;
}

export interface AccommodationResult {
  place_id: string;
  name: string;
  address: string;
  lat: number | null;
  lon: number | null;
  rating: number | null;
  review_count: number | null;
  price_level: number | null;
  types: string[];
  photo_url: string | null;
  business_status: string;
  google_maps_url: string;
  tripadvisor_url: string;
  match_score?: number;
  why_good?: string;
  concerns?: string | null;
  property_type?: string;
  tripadvisor_rating?: number | string | null;
  tripadvisor_summary?: string | null;
}

export interface AccommodationFinderResponse {
  destination: string;
  center_lat: number;
  center_lon: number;
  zoom: number;
  area_summary: string;
  results: AccommodationResult[];
  total_found: number;
  parking_notes: string | null;
  area_tips: string | null;
  booking_tip: string | null;
  has_google_api: boolean;
}

// Ground Transportation API functions
export const transportAPI = {
  rentalCars: async (data: { destination: string; trip_type?: string }) => {
    const response = await apiClient.post('/transport/rental-cars', data);
    return response.data;
  },

  drivingInfo: async (data: { destination: string }) => {
    const response = await apiClient.post('/transport/driving-info', data);
    return response.data;
  },

  publicTransit: async (data: { destination: string }) => {
    const response = await apiClient.post('/transport/public-transit', data);
    return response.data;
  },

  rideshareTaxis: async (data: { destination: string }) => {
    const response = await apiClient.post('/transport/rideshare-taxis', data);
    return response.data;
  },

  trains: async (data: { destination: string; region?: string }) => {
    const response = await apiClient.post('/transport/trains', data);
    return response.data;
  },
};

// Cultural Intelligence Hub API functions
export const culturalAPI = {
  etiquette: async (data: { destination: string }) => {
    const response = await apiClient.post('/culture/etiquette', data);
    return response.data;
  },

  foodDrink: async (data: { destination: string; dietary_restrictions?: string[] }) => {
    const response = await apiClient.post('/culture/food-drink', data);
    return response.data;
  },

  language: async (data: { destination: string }) => {
    const response = await apiClient.post('/culture/language', data);
    return response.data;
  },

  festivals: async (data: { destination: string; travel_month?: string }) => {
    const response = await apiClient.post('/culture/festivals', data);
    return response.data;
  },

  safety: async (data: { destination: string }) => {
    const response = await apiClient.post('/culture/safety', data);
    return response.data;
  },
};

// Phrase Book API functions
export const phrasebookAPI = {
  languageInfo: async (data: { destination: string }) => {
    const response = await apiClient.post('/phrasebook/language-info', data);
    return response.data;
  },

  phrases: async (data: { destination: string; language?: string; category?: string }) => {
    const response = await apiClient.post('/phrasebook/phrases', data);
    return response.data;
  },

  translate: async (data: { destination: string; language?: string; phrase: string }) => {
    const response = await apiClient.post('/phrasebook/translate', data);
    return response.data;
  },
};

// Cuisine Intelligence API functions
export const cuisineAPI = {
  guide: async (data: { destination: string; dietary_restrictions?: string[] }) => {
    const response = await apiClient.post('/cuisine/guide', data);
    return response.data;
  },

  drinks: async (data: { destination: string; include_alcoholic?: boolean; include_non_alcoholic?: boolean }) => {
    const response = await apiClient.post('/cuisine/drinks', data);
    return response.data;
  },

  restaurants: async (data: { destination: string; dietary_restrictions?: string[] }) => {
    const response = await apiClient.post('/cuisine/restaurants', data);
    return response.data;
  },
};

// ─── Itinerary V2 (Stay-based) API ───────────────────────────────────────────

export interface ItinLocation {
  destination_id?: string;
  city: string;
  country: string;
  lat?: number;
  lon?: number;
  is_custom?: boolean;
}

export interface LodgingRec {
  name: string;
  type: string;
  area: string;
  description: string;
  price_level: string;
  search_url: string;
}

export interface LodgingInfo {
  specified_name?: string;
  specified_address?: string;
  specified_url?: string;
  want_recommendations: boolean;
  recommendations?: LodgingRec[];
}

export interface DiningRec {
  name: string;
  cuisine: string;
  area: string;
  description: string;
  price_level: string;
  url?: string;
}

export interface ItinActivity {
  id: string;
  date: string;
  time_slot: 'morning' | 'afternoon' | 'evening';
  title: string;
  description: string;
  duration_hours: number;
  category: string;
  url?: string;
  estimated_cost_usd?: number;
  lat?: number;
  lon?: number;
  place_id?: string;
  google_maps_uri?: string;
  coords_source?: string;
}

export interface Stay {
  type: 'stay';
  id: string;
  location: ItinLocation;
  check_in: string;
  check_out: string;
  nights: number;
  lodging: LodgingInfo;
  activities: ItinActivity[];
  dining_recommendations: DiningRec[];
  travel_tips?: string[];
  notes?: string;
  enriched: boolean;
}

export interface RouteSight {
  id?: string;
  name: string;
  country?: string;
  lat: number;
  lon: number;
  category: string;
  description: string;
  why_worth_it?: string;
  detour_minutes?: number;
  time_needed_hours?: number;
  url?: string;
  place_id?: string;
  google_maps_uri?: string;
  coords_source?: string;
}

export type TransitMode = 'drive' | 'fly' | 'train' | 'bus' | 'ferry';

export interface TransitSegment {
  type: 'transit';
  id: string;
  from_location: ItinLocation;
  to_location: ItinLocation;
  departure_time: string;
  arrival_time: string;
  mode: TransitMode;
  duration_minutes?: number;
  distance_km?: number;
  geometry?: [number, number][];
  notes?: string;
  departure_override?: boolean;
  route_sights?: RouteSight[];
  route_sights_loaded?: boolean;
}

export interface SideTripSuggestion {
  id: string;
  destination: string;
  country: string;
  lat?: number;
  lon?: number;
  driving_time_minutes?: number;
  distance_km?: number;
  duration: 'half-day' | 'full-day';
  description: string;
  highlights: string[];
  practical_tips?: string[];
  border_crossing?: boolean;
  border_notes?: string;
  best_for?: string;
}

export interface SideTrip {
  id: string;
  from_stay_id: string;
  suggestion: SideTripSuggestion;
  date: string;
  confirmed: boolean;
}

export interface TravelRequirement {
  country: string;
  country_code?: string;
  currency?: string;
  language?: string;
  eu_member?: boolean;
  schengen_member?: boolean;
  visa_info: string;
  entry_requirements?: string;
  safety_rating?: string;
  safety_notes?: string;
  practical_tips?: string[];
  border_notes?: string;
  advisory_url?: string;
  source?: string;
}

export interface PlanPreferences {
  travel_style: string;
  budget_level: string;
  group_type: string;
  interests: string[];
  allowed_modes: TransitMode[];
  default_mode: TransitMode;
  start_date: string;
}

export interface ItineraryV2Data {
  title: string;
  nationality?: string;
  segments: (Stay | TransitSegment)[];
  side_trips: SideTrip[];
  travel_requirements?: TravelRequirement[];
  plan_preferences?: PlanPreferences;
}

export interface ItinerarySummary {
  id: string;
  title: string;
  city_count: number;
  cities: string[];
  created_at: string;
  updated_at: string;
}

export const itineraryV2API = {
  list: async (): Promise<ItinerarySummary[]> => {
    const response = await apiClient.get('/itineraries-v2');
    return response.data;
  },

  create: async (data: { title: string; data: ItineraryV2Data }) => {
    const response = await apiClient.post('/itineraries-v2', data);
    return response.data;
  },

  get: async (id: string) => {
    const response = await apiClient.get(`/itineraries-v2/${id}`);
    return response.data;
  },

  update: async (id: string, data: { title?: string; data?: ItineraryV2Data }) => {
    const response = await apiClient.put(`/itineraries-v2/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/itineraries-v2/${id}`);
  },

  geocode: async (city: string, country: string) => {
    const response = await apiClient.post('/itineraries-v2/geocode', { city, country });
    return response.data as { lat: number; lon: number; display_name?: string; source: string };
  },

  calculateTransit: async (params: {
    from_lat: number; from_lon: number;
    to_lat: number; to_lon: number;
    mode?: TransitMode;
  }) => {
    const response = await apiClient.post('/itineraries-v2/calculate-transit', params);
    return response.data as {
      mode: TransitMode;
      duration_minutes: number;
      distance_km: number;
      geometry?: [number, number][];
      note?: string;
    };
  },

  enrichStay: async (params: {
    city: string; country: string; lat?: number; lon?: number;
    check_in: string; check_out: string;
    travel_style?: string; budget_level?: string;
    interests?: string[]; group_type?: string;
    notes?: string; want_lodging?: boolean; want_dining?: boolean;
  }) => {
    const response = await apiClient.post('/itineraries-v2/enrich-stay', params);
    return response.data as {
      activities: ItinActivity[];
      lodging_recommendations: LodgingRec[];
      dining_recommendations: DiningRec[];
      travel_tips: string[];
      available_slots: Array<{ date: string; day_label: string; slots: string[] }>;
    };
  },

  sideTripSuggestions: async (params: {
    from_city: string; from_country: string;
    from_lat?: number; from_lon?: number;
    duration_preference?: string;
  }) => {
    const response = await apiClient.post('/itineraries-v2/side-trip-suggestions', params);
    return response.data as { suggestions: SideTripSuggestion[] };
  },

  routeSights: async (params: {
    from_city: string; from_country: string;
    to_city: string; to_country: string;
    from_lat?: number; from_lon?: number;
    to_lat?: number; to_lon?: number;
    mode?: TransitMode;
    max_detour_minutes?: number;
    interests?: string[];
  }) => {
    const response = await apiClient.post('/itineraries-v2/route-sights', params);
    return response.data as { sights: RouteSight[] };
  },

  travelRequirements: async (countries: string[]) => {
    const response = await apiClient.post('/itineraries-v2/travel-requirements', { countries });
    return response.data as { requirements: TravelRequirement[] };
  },
};

// ─── Trip Briefing API ───────────────────────────────────────────────────────

export interface BriefingStayHighlight {
  title: string;
  description?: string;
  time_slot?: string;
  duration_hours?: number;
  estimated_cost_usd?: number;
  url?: string;
  google_maps_uri?: string;
}

export interface BriefingStay {
  city: string;
  country: string;
  check_in: string;
  check_out: string;
  nights: number;
  enriched: boolean;
  highlights: BriefingStayHighlight[];
  lodging?: LodgingRec | null;
  dining: DiningRec[];
  travel_tips: string[];
}

export interface BriefingTransit {
  from_city: string;
  to_city: string;
  mode: TransitMode;
  duration_minutes?: number;
  distance_km?: number;
  departure_time?: string;
  arrival_time?: string;
  route_sights: Array<{ name: string; why: string }>;
}

export interface BriefingWeather {
  city: string;
  available: boolean;
  too_far?: boolean;
  date_range?: string;
  temp_c_min?: number;
  temp_c_max?: number;
  precip_days?: number;
  total_days?: number;
}

export interface BriefingCountry {
  country: string;
  country_code?: string;
  currency?: string;
  language?: string;
  eu_member?: boolean;
  schengen_member?: boolean;
  visa_info?: string;
  entry_requirements?: string;
  safety_rating?: string;
  safety_notes?: string;
  practical_tips?: string[];
  advisory_url?: string;
  emergency_number?: string;
  source?: string;
}

export interface BriefingCulture {
  country: string;
  greetings?: string;
  tipping?: string;
  dining_note?: string;
  dos?: string[];
  donts?: string[];
}

export interface BriefingPhrases {
  country: string;
  language?: string;
  phrases: Array<{ english: string; local: string; pronunciation?: string }>;
}

export interface BriefingPacking {
  categories: Array<{ name: string; items: string[] }>;
}

export interface BriefingAgendaDay {
  date: string;
  kind: 'stay' | 'transit';
  title: string;
  city?: string;
  mode?: string;
  duration_minutes?: number;
  distance_km?: number;
  departure_time?: string;
  arrival_time?: string;
  check_in_time?: string | null;
  check_out_time?: string | null;
  activity_count?: number;
  activities?: Array<{ title: string; time_slot?: string; duration_hours?: number }>;
}

export interface TripBriefingData {
  trip_title: string;
  generated_at: string;
  overview: {
    start_date: string;
    end_date: string;
    total_nights: number;
    countries: string[];
    cities: string[];
    stay_count: number;
    total_drive_km: number;
    total_drive_minutes: number;
  };
  stays: BriefingStay[];
  transits: BriefingTransit[];
  weather: BriefingWeather[];
  countries: BriefingCountry[];
  culture: BriefingCulture[];
  phrases: BriefingPhrases[];
  packing: BriefingPacking;
  agenda: BriefingAgendaDay[];
  preferences: Record<string, any>;
}

export const briefingAPI = {
  generate: async (params: {
    itinerary_id?: string;
    itinerary_data?: any;
    include_culture?: boolean;
    include_phrases?: boolean;
    include_packing?: boolean;
    include_weather?: boolean;
  }): Promise<TripBriefingData> => {
    const response = await apiClient.post('/briefing/generate', params);
    return response.data;
  },

  exportDocument: async (briefing: TripBriefingData, format: 'pdf' | 'docx' | 'txt'): Promise<Blob> => {
    const response = await apiClient.post(
      '/briefing/export',
      { briefing, format },
      { responseType: 'blob' },
    );
    return response.data;
  },
};

// Classify API
export const classifyAPI = {
  classifyQuery: async (query: string) => {
    const response = await apiClient.post('/classify/query', { query });
    return response.data;
  },
};

// Feedback API
export const feedbackAPI = {
  submit: async (data: {
    feedback_type?: string | null;
    feature_area?: string | null;
    comment: string;
  }) => {
    const response = await apiClient.post('/feedback/submit', data);
    return response.data;
  },
};

// Export API
export interface ExportRequest {
  content: string;
  title: string;
  format: 'docx' | 'pdf' | 'txt';
}

export const exportAPI = {
  exportDocument: async (request: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/export/document', request, {
      responseType: 'blob',
    });
    return response.data;
  },
};
